import { useState } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { Loading } from 'react-admin';
import { RequireAccess } from '../auth/RequireAccess';
import { ChargebackRow } from './ChargebackRow';
import { useChargebacks } from './useChargebacks';
import type { DisputeRow } from './chargebacksModel';

/**
 * Chargebacks admin (D-chargebacks, Phase 5 surface 4). The disputed-transaction
 * lifecycle worklist: a dispute is orthogonal to settlement status, and resolving it
 * LOST claws back the cashback as a NEGATIVE LEDGER ENTRY (the reversal), never a
 * direct balance write. This surface establishes the ledger-entry discipline manual
 * balance adjustment inherits.
 *
 * A filtered view over disputed transactions (the manual-attribution queue idiom),
 * organised by the dispute lifecycle: received needing attention, under review in
 * progress, and resolved as history.
 *
 * OPERATIONAL, SWITCHER-SCOPED (the deliberate divergence from the fraud-class global
 * rule): a chargeback is a cashout-shaped thing (one transaction, one member, one
 * market), so it follows the region switcher scope like the cashout queue, NOT a
 * local investigative facet. ROLE-gated to fraud and super_admin (the reviewers who
 * work it), so a viewer's read-everywhere wildcard cannot reach it.
 */

const Section = ({
  title,
  rows,
  emptyText,
  testId,
  now,
}: {
  title: string;
  rows: DisputeRow[];
  emptyText: string;
  testId: string;
  now: number;
}) => (
  <Box sx={{ mb: 4 }} data-testid={testId}>
    <Typography variant="subtitle2" sx={{ mb: 1 }}>
      {title} ({rows.length})
    </Typography>
    {rows.length === 0 ? (
      <Typography variant="body2" color="text.secondary">
        {emptyText}
      </Typography>
    ) : (
      rows.map((row) => <ChargebackRow key={row.id} row={row} now={now} />)
    )}
  </Box>
);

/** The surface without the access wrapper. Exported so tests can inject `asOf`
 *  (the clock a resolution is stamped with). */
export const ChargebacksView = ({ asOf }: { asOf?: number }) => {
  const [now] = useState<number>(() => asOf ?? Date.now());
  const { view, isLoading } = useChargebacks();

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ letterSpacing: 1 }}
      >
        Trust and safety
      </Typography>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
        Chargebacks
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Disputed orders as a worklist. A dispute resolved lost claws back the
        cashback as a ledger entry the balance derives from, never a direct
        change to a balance, and the order stays confirmed. This view follows
        the region switcher scope.
      </Typography>

      {isLoading || !view ? (
        <Loading />
      ) : (
        <>
          <Section
            title="Received"
            rows={view.received}
            emptyText="No disputes received in scope."
            testId="chargeback-section-received"
            now={now}
          />
          <Divider sx={{ mb: 3 }} />
          <Section
            title="Under review"
            rows={view.underReview}
            emptyText="No disputes under review in scope."
            testId="chargeback-section-under-review"
            now={now}
          />
          <Divider sx={{ mb: 3 }} />
          <Section
            title="Resolved"
            rows={view.resolved}
            emptyText="No resolved disputes in scope."
            testId="chargeback-section-resolved"
            now={now}
          />
        </>
      )}
    </Box>
  );
};

/**
 * Gated entry registered at /chargebacks. ROLE-gated to fraud and super_admin: the
 * dispute work is sensitive financial work, so a viewer's read-everywhere wildcard
 * must not reach it and an editor has no business here (D-chargebacks).
 */
export const Chargebacks = () => (
  <RequireAccess roles={['fraud', 'super_admin']}>
    <ChargebacksView />
  </RequireAccess>
);
